/* eslint-disable @typescript-eslint/no-explicit-any */
import dynamic from 'next/dynamic';
import Header from '@/components/Internals/Header';
import WrapperContent from '@/components/Layout/WrapperContent';
import { AdContainer } from '@/components/Layout/ItemListLastNews/styles';
import AdsGeneratorMemo from '@/components/Ads/Google/AdsTypeGeneratorsMemo';
import { getCurrentHub } from '@sentry/nextjs';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import useBreakpoint from '@/hooks/useBreakpoint';
import { GridInternal } from './styles';
import MainContent from '../MainContent';
import { SponseredCategoryHeader } from '@/components/Layout/SponsoredCategoryHeader';
import { getEditorialCategoryWithStringToInternal, getSponsoredCategoryOrTypeWithStringToInternal, isSponsoredOrEditorialToInternal } from '@/lib/sponsors';

interface NewItemProps {
  newsPreview?: boolean;
  newsData: any;
  isCurrentPage?: boolean;
  type?: string;
}

export default function NewsItem({ newsData, newsPreview = false, isCurrentPage = false, type = '' }: NewItemProps) {
  const [isTabletOrMobile] = useBreakpoint('tablet');
  const currentSpan = getCurrentHub().getScope()?.getSpan();
  const newsItemSpan = currentSpan && currentSpan.startChild({ op: 'newsItem' });
  const SideContent = dynamic(() => import('@/components/Internals/SideContent'));
  const router = useRouter();
  const { capa, conteudo, podcast, url } = newsData;
  const pageUri = url;
  const IdsPg = newsData.widget && newsData.widget?.tipo === 'produto' && type === 'analise' ? [newsData.cu, newsData.widget?.dados?.cu] : newsData.cu;

  let categories: string[] = undefined;
  if (newsData?.categorias) categories = newsData?.categorias;
  const mainCategory: string = newsData?.categoriaPrincipal || undefined;
  const mainCategoryId: number = newsData?.idCategoriaPrincipal || undefined;

  const publicationType = newsData?.tipoPublicacao;

  // eslint-disable-next-line no-bitwise
  const isAdvertorial = type === 'artigo' && (newsData.flags & 2) !== 0;
  const isOffer = type === 'artigo' && (newsData.flags & 32) !== 0;

  const socialMediaCategoryId = 36;
  const membranaCategory = mainCategoryId === socialMediaCategoryId;

  let isSponsoredOrEditorialCategory = undefined;
  let editorialCategory = undefined;
  let sponsoredCategory = undefined;

  if (categories && mainCategory) {
    isSponsoredOrEditorialCategory = isSponsoredOrEditorialToInternal(mainCategory, categories, publicationType);
    if (categories && isSponsoredOrEditorialCategory === 'editorial') {
      editorialCategory = getEditorialCategoryWithStringToInternal(categories, mainCategory);
    }

    if ((categories || publicationType) && isSponsoredOrEditorialCategory === 'sponsored') {
      sponsoredCategory = getSponsoredCategoryOrTypeWithStringToInternal(categories, mainCategory, publicationType);
    }
  }

  const shouldRenderAdsGoogle = !isAdvertorial;
  const shouldRenderAdsByType = type !== 'video' && type !== 'podcast' && type !== 'analise' && type !== 'artigo';

  const enableNewsLetter =
    newsData?.tipoPublicacao !== '8' && // TIPO_ARTIGOS
    newsData?.tipoPublicacao !== '11' && // TIPO_LISTAS
    newsData?.tipoPublicacao !== '2' && // TIPO_ANALISES
    newsData?.tipoPublicacao !== '4' && // TIPO_TUTORIAIS
    !isAdvertorial && // Publieditorial é uma flag na matéria.
    !isOffer; // Oferta é uma flag na matéria.

  useEffect(() => {
    const navigate = () => {
      router.push(window.location.href);
    };

    window.onpopstate = navigate;
  });

  return (
    <div className="route-uri" data-uri-page={pageUri}>
      <WrapperContent isOnHeader hasBreadcrumb>
        {isSponsoredOrEditorialCategory === 'sponsored' && <SponseredCategoryHeader sponsoredCategory={sponsoredCategory} />}
        <Header
          breadcrumb={newsData.breadcrumb}
          titulo={newsData.titulo}
          publicacao={newsData.publicacao}
          isAdvertorial={isAdvertorial}
          isSubDomain={Boolean(newsData.dominio)}
          type={type}
        />

        <GridInternal>
          <MainContent
            headerImgData={{ url: capa.url, caption: capa.creditos, blurhash: capa.blurhash, srcset: capa.srcset }}
            article={conteudo}
            mainCategory={mainCategory}
            widget={newsData?.widget || conteudo.widget}
            video={newsData?.video}
            citados={newsData?.citados}
            capa={newsData.capa}
            podcast={podcast}
            titulo={newsData.titulo}
            description={newsData.tituloAlt}
            url={newsData.encurtador}
            type={type}
            placeholderAds={newsPreview}
            shouldRenderAdsGoogle={shouldRenderAdsGoogle}
            widgetBf={newsData?.conteudo?.blocoBlackFriday}
            isSubDomain={Boolean(newsData.dominio)}
            flags={newsData.flags}
            precos={newsData?.precos}
            editorialCategory={editorialCategory}
            isHeader="internal"
            isNewsOpen={isAdvertorial}
            useMembrana={membranaCategory}
          />
          {!isTabletOrMobile && <SideContent placeholderAds={newsPreview} shouldRenderAds={shouldRenderAdsGoogle} enableNewsLetter={enableNewsLetter}/>}
        </GridInternal>
      </WrapperContent>
      {!newsPreview && shouldRenderAdsByType && (
        <AdContainer>
          {shouldRenderAdsByType && (
            <>
              <AdsGeneratorMemo type="billboard/footer" />
              <AdsGeneratorMemo type="rectangle/footer" />
            </>
          )}
        </AdContainer>
      )}
      {newsItemSpan && newsItemSpan.finish()}
    </div>
  );
}
